/**
 * Light / dark / system color mode (persisted in localStorage as "cf.colorMode").
 *
 *   <ColorModeProvider>…</ColorModeProvider>          // wraps MUI ThemeProvider + CssBaseline
 *   const { mode, resolved, setMode } = useColorMode(); // mode: "light" | "dark" | "system"; resolved: "light" | "dark"
 */
import { CssBaseline, ThemeProvider, useMediaQuery } from "@mui/material";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { buildTheme } from "./theme";

export type ColorMode = "light" | "dark" | "system";
export const COLOR_MODE_KEY = "cf.colorMode";

type Ctx = { mode: ColorMode; resolved: "light" | "dark"; setMode: (mode: ColorMode) => void };
const ColorModeContext = createContext<Ctx>({ mode: "system", resolved: "light", setMode: () => undefined });

function readStored(): ColorMode {
  try {
    const v = localStorage.getItem(COLOR_MODE_KEY);
    return v === "light" || v === "dark" || v === "system" ? v : "system";
  } catch { return "system"; }
}

export function ColorModeProvider({ children, initialMode }: { children: ReactNode; initialMode?: ColorMode }) {
  const [mode, setModeState] = useState<ColorMode>(() => initialMode ?? readStored());
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const resolved = mode === "system" ? (prefersDark ? "dark" : "light") : mode;
  const setMode = useCallback((next: ColorMode) => {
    setModeState(next);
    try { localStorage.setItem(COLOR_MODE_KEY, next); } catch { /* ignore */ }
  }, []);
  useEffect(() => { document.documentElement.dataset.theme = resolved; }, [resolved]);
  const theme = useMemo(() => buildTheme(resolved), [resolved]);
  const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode]);
  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export function useColorMode() { return useContext(ColorModeContext); }
